import type { QueryClient } from '@tanstack/react-query'
import { createRouter, type RouterHistory } from '@tanstack/react-router'
import { dismissBootScreen } from '@volee/ui'

import { ME_KEY } from './auth/me.ts'
import { onSessionLost } from './services/api.ts'
import { signInRoute } from './routes/sign-in.tsx'
import { signUpRoute } from './routes/sign-up.tsx'
import { signUpClubRoute } from './routes/sign-up-club.tsx'
import { signUpParentRoute } from './routes/sign-up-parent.tsx'
import { signUpPlayerRoute } from './routes/sign-up-player.tsx'
import { registrationStatusRoute } from './routes/registration-status.tsx'
import { familyEnrolmentStatusRoute } from './routes/family-enrolment-status.tsx'
import { autonomyRoute } from './routes/autonomy.tsx'
import { forgotPasswordRoute } from './routes/forgot-password.tsx'
import { activationRoute } from './routes/activation.tsx'
import { iscrizioneRoute } from './routes/iscrizione.tsx'
import { guardianLinkRoute } from './routes/guardian-link.tsx'
import { forbiddenRoute } from './routes/forbidden.tsx'
import { callbackRoute } from './routes/callback.tsx'
import { playersRoute } from './routes/players.tsx'
import { playerRoute } from './routes/player.tsx'
import { playerHighlightsRoute } from './routes/player-highlights.tsx'
import { myOfficialRoute } from './routes/my-official.tsx'
import { officialPeopleRoute } from './routes/my-official/people.tsx'
import { officialInviteRoute } from './routes/my-official/invite.tsx'
import { officialPersonRoute } from './routes/my-official/person.tsx'
import { officialClubRoute } from './routes/my-official/club.tsx'
import { officialPitchesRoute } from './routes/my-official/pitches.tsx'
import { officialPlayersRoute } from './routes/my-official/players.tsx'
import { officialPlayerRoute } from './routes/my-official/player.tsx'
import { officialNewPlayerRoute } from './routes/my-official/new-player.tsx'
import { officialTabulatoRoute } from './routes/my-official/tabulato.tsx'
import { officialRequestsRoute } from './routes/my-official/requests.tsx'
import { officialRequestRoute } from './routes/my-official/request.tsx'
import { officialLeveRoute } from './routes/my-official/leve.tsx'
import { myCoachRoute } from './routes/my-coach.tsx'
import { myParentRoute } from './routes/my-parent.tsx'
import { myParentAddChildRoute } from './routes/my-parent-add-child.tsx'
import { myPlayerRoute } from './routes/my-player.tsx'
import { myPlayerEditRoute } from './routes/my-player-edit.tsx'
import { settingsRoute } from './routes/settings.tsx'
import { subscriptionsRoute } from './routes/subscriptions.tsx'
import { subscriptionCheckoutRoute } from './routes/subscription-checkout.tsx'
import { entryRoute } from './routes/entry.tsx'
import { homeRoute } from './routes/home.tsx'
import { rootRoute } from './routes/root.tsx'
import { clubsRoute } from './routes/clubs.tsx'
import { clubRoute } from './routes/club.tsx'
import { clubKitsRoute } from './routes/club-kits.tsx'
import { teamsRoute } from './routes/teams.tsx'
import { teamDetailRoute } from './routes/team-detail.tsx'
import { teamKitsRoute } from './routes/team-kits.tsx'
import { leagueGroupRoute, leaguesRoute } from './routes/leagues.tsx'
import { highlightRoute, lineupRoute, matchRoute } from './routes/match.tsx'
import { matchesRoute } from './routes/matches.tsx'

// Declared by hand: no file-route codegen plugin
// (apps/frontend/docs/razionale-che-il-codice-indica.md).
const routeTree = rootRoute.addChildren([
  entryRoute.addChildren([
    homeRoute,
    signInRoute,
    forgotPasswordRoute,
    signUpRoute,
    signUpClubRoute,
    signUpPlayerRoute,
    signUpParentRoute,
    activationRoute,
    iscrizioneRoute,
    guardianLinkRoute,
  ]),
  registrationStatusRoute,
  familyEnrolmentStatusRoute,
  autonomyRoute,
  callbackRoute,
  forbiddenRoute,
  clubsRoute,
  clubRoute,
  clubKitsRoute,
  myOfficialRoute,
  officialPeopleRoute.addChildren([officialInviteRoute]),
  officialPersonRoute,
  officialClubRoute,
  officialPitchesRoute,
  officialPlayersRoute.addChildren([officialNewPlayerRoute]),
  officialTabulatoRoute,
  officialPlayerRoute,
  officialRequestsRoute,
  officialRequestRoute,
  officialLeveRoute,
  myCoachRoute,
  myParentRoute,
  myParentAddChildRoute,
  myPlayerRoute,
  myPlayerEditRoute,
  settingsRoute,
  subscriptionsRoute,
  subscriptionCheckoutRoute,
  playersRoute,
  playerRoute,
  playerHighlightsRoute,
  teamsRoute,
  teamDetailRoute,
  teamKitsRoute,
  matchRoute,
  matchesRoute,
  leaguesRoute,
  leagueGroupRoute,
  lineupRoute,
  highlightRoute,
])

// `history` is injectable for the tests: `createMemoryHistory` mounts the app on a path with
// no browser, which is what lets a page be tested against the real route tree.
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  const router = createRouter({
    routeTree: routeTree,
    context: { queryClient },
    history,
    // With a session already cached, the guard must not flash a spinner on every
    // transition.
    defaultPreload: 'intent',
  })

  // After the first route's `beforeLoad`, on whatever page it ends on (a redirect, a not-found, an
  // error): until then the router draws nothing, and the empty ground would show.
  const stopWatchingBoot = router.subscribe('onResolved', () => {
    stopWatchingBoot()
    dismissBootScreen()
  })

  // A session can end during a call, not only on a transition: when api-client declares it
  // lost, we go to the login.
  onSessionLost(() => {
    // Dropped, or the login's `sendInsidersOn` finds the cached session and sends the visitor
    // straight back to the page that just lost it: the reason would never be shown.
    queryClient.removeQueries({ queryKey: ME_KEY })
    void router.navigate({
      to: signInRoute.to,
      search: { returnTo: router.state.location.href, reason: 'session-expired' },
    })
  })

  return router
}

export type Router = ReturnType<typeof createAppRouter>

// What types `Link to=`, the search params and `navigate` app-wide — the reason the router is
// TanStack (apps/frontend/docs/razionale-che-il-codice-indica.md).
declare module '@tanstack/react-router' {
  interface Register {
    router: Router
  }

  // What the shell draws around a route; `bare` is a page reached before the app is usable, `flow`
  // a task with its own bar at the foot: the app's header stays, the navigation goes.
  interface StaticDataRouteOption {
    // `entry`, on the entrance's layout route: apps/frontend/docs/ingresso-a-tutto-schermo.md
    chrome?: 'app' | 'bare' | 'entry' | 'flow'
    // The one entrance route with no sheet over it: where the sheet is closed.
    entryDoor?: boolean
    // The shell reserves nothing above it: packages/ui/docs/barra-in-basso-app-shell.md
    opensOnHero?: boolean
    // A `TheaterPage`: from lg the column narrows while the reader stays, unremembered.
    theater?: boolean
  }
}
